import type { BrainComposerCollection } from "./brainComposerCollection.ts";
import { brainDeleteTargetPath, usableBrainTextSources } from "./brainComposerCollection.ts";
import { actionSkills, findAction } from "@afar-research/agent-content/actions";

import type { BrainAction } from "./brainAction.ts";
import { withComposerSkillTokens } from "./composerSkillToken.ts";

const BRAIN_ACTION_IDS: Readonly<Record<BrainAction, string>> = {
  ingest: "ingest",
  lint: "brain-lint",
  delete: "brain-delete",
};

/**
 * The App-owned Skill Bundles a Brain Action runs: the `brain` workspace
 * knowledge, then the operation's own bundle. The one binding behind both
 * halves of a send: the chips this module writes into the prompt, and the
 * provider-native dispatch the composer resolves against the skill catalog. A
 * prompt that carries a chip without the dispatch leaves the agent to honour a
 * `$name` by reading it, which is not an invocation.
 */
export function brainActionSkillBundleIds(action: BrainAction): ReadonlyArray<string> {
  const definition = findAction(BRAIN_ACTION_IDS[action]);
  return definition === undefined ? [] : actionSkills(definition);
}

/** What the mode's chips act on, one numbered line each, in submission order. */
function describeBrainComposerCollection(
  mode: BrainAction,
  collection: BrainComposerCollection,
): string {
  if (mode === "delete") {
    return [...new Set(collection.deleteTargets)]
      .map((target, index) => `${index + 1}. ${brainDeleteTargetPath(target)}`)
      .join("\n");
  }
  const attachmentLines = collection.attachments.map((attachment, index) => {
    const kind = attachment.isFolder ? "folder" : attachment.hostPath ? "file" : "attachment";
    const pathSuffix = attachment.hostPath ? ` path: ${attachment.hostPath}` : "";
    return `${index + 1}. [${kind}] ${attachment.name}${pathSuffix} (attachment ${index + 1}) [source-id: ${attachment.id}]`;
  });
  const textLines = usableBrainTextSources(collection).map(
    (source, index) =>
      `${attachmentLines.length + index + 1}. [text] ${source.text.trim()} [source-id: ${source.id}]`,
  );
  return [...attachmentLines, ...textLines].join("\n");
}

/** How many sources an Ingest prompt announces. */
function countBrainIngestSources(collection: BrainComposerCollection): number {
  return collection.attachments.length + usableBrainTextSources(collection).length;
}

/**
 * The one thing a Brain Action changes about a send: the composer arranges the
 * prompt from the Action and its chips, behind the Action's skill tokens. The
 * turn itself is ordinary — no operation identity, no git baseline, no
 * verification pass.
 *
 * The arranged prompt is built here rather than written into the composer on
 * the way to a send: the composer holds what the user typed, and a send that
 * borrowed it to carry the arrangement repainted the composer with text nobody
 * typed.
 */
export function formatBrainActionPrompt(input: {
  readonly action: BrainAction;
  readonly collection: BrainComposerCollection;
  readonly text: string;
}): string {
  const skills = brainActionSkillBundleIds(input.action);
  if (input.action === "lint") {
    return withComposerSkillTokens(skills, ["Brain action: Lint", "Scope: Complete Second Brain"]);
  }
  if (input.action === "delete") {
    const targets = [...new Set(input.collection.deleteTargets)];
    return withComposerSkillTokens(skills, [
      "Brain action: Delete",
      `Targets (${targets.length}, in order):`,
      describeBrainComposerCollection("delete", input.collection),
      "Each target is a whole capture folder under .afar-research/brain/. Delete it entirely; never delete part of one.",
      "Then repair the wiki in place: for every .afar-research/brain/wiki/ page citing a deleted capture, drop the dead links and rewrite or mark as unsourced any claim that cited only that capture. Never delete a wiki page because its source is gone.",
      "Update .afar-research/brain/wiki/index.md where pages changed materially, and append a dated delete entry to .afar-research/brain/wiki/log.md.",
      "A target that is already missing, or that nothing cites, is a report line rather than a failure. Report it and continue with the remaining targets.",
    ]);
  }
  const note = input.text.trim();
  return withComposerSkillTokens(skills, [
    "Brain action: Ingest",
    `Sources (${countBrainIngestSources(input.collection)}, in order):`,
    describeBrainComposerCollection("ingest", input.collection),
    "Listed files, folders, and text are ingest sources with their paths. Decide what, if anything, to copy into .afar-research/brain/raw/; do not copy a whole folder tree unless that material belongs in the Brain. Fetch URL material, and write only [text] sources from the supplied paste. Do not ask the user to copy or paste material you can capture.",
    "Process each source sequentially and report its raw and wiki artifacts separately.",
    ...(note.length > 0 ? ["", note] : []),
  ]);
}
